<template>
  <base-card no-header>
    <template #body>
      <el-row class="items-stretch broker-row">
        <el-col :span="6">
          <h3>
            {{
              Object.keys(ApiName).find(
                key => ApiName[key] === configuration.apiName
              )
            }}
          </h3>
          <h3 class="body-1 cursor-pointer mt-1" @click="openVersionModal">
            {{ $t('currentVersion') + ': ' + configuration.version }}
          </h3>
          <el-tooltip placement="top" :content="$t('disableApiConnectionNote')">
            <el-switch
              v-model="form.enabled"
              :active-text="$t('enabledCapitalized')"
              :inactive-text="$t('disabledCapitalized')"
              @change="handleChange"
            />
          </el-tooltip>
        </el-col>
        <el-col :span="15">
          <el-form
            ref="formRef"
            :model="form"
            :rules="formRules"
            label-position="top"
          >
            <el-divider>{{ $t('connectionDetails') }}</el-divider>
            <el-form-item
              v-if="props.configuration.apiName !== ApiName.Etrade"
              class="mb-0"
              :label="$t('dataEndpoint')"
              prop="dataEndpoint"
            >
              <el-input
                v-model.trim="form.dataEndpoint"
                :validate-event="false"
                size="small"
                @change="handleChange"
              />
            </el-form-item>
            <el-form-item class="mb-0" :label="$t('clientId')" prop="clientId">
              <el-input
                v-model="form.clientId"
                :validate-event="false"
                size="small"
                @change="handleChange"
              />
            </el-form-item>
            <el-form-item
              class="mb-0"
              :label="$t('clientSecretKeyName')"
              prop="clientSecretKeyName"
            >
              <el-input
                v-model="form.clientSecretKeyName"
                :validate-event="false"
                size="small"
                @change="handleChange"
              />
            </el-form-item>
            <el-divider>{{ $t('tokenAcquisitionSettings') }}</el-divider>
            <el-form-item
              class="mb-0"
              :label="$t('tokenUrl')"
              :validate-event="false"
              prop="tokenUrl"
            >
              <el-input
                v-model.trim="form.tokenUrl"
                :validate-event="false"
                size="small"
                @change="handleChange"
              />
            </el-form-item>
            <el-form-item class="mb-0" :label="$t('scope')" prop="scope">
              <el-input
                v-model="form.scope"
                :validate-event="false"
                size="small"
                @change="handleChange"
              />
            </el-form-item>
            <el-form-item
              class="mb-0"
              :label="$t('grantType')"
              prop="grantType"
            >
              <el-select
                v-model="form.grantType"
                size="small"
                @change="handleChange"
              >
                <el-option
                  v-for="item in Object.keys(AuthGrantType)"
                  :key="item ?? ''"
                  :label="item ?? ''"
                  :value="item"
                ></el-option>
              </el-select>
            </el-form-item>
            <el-form-item
              class="mb-0"
              :label="$t('responseType')"
              prop="responseType"
            >
              <el-select
                v-model="form.responseType"
                size="small"
                @change="handleChange"
              >
                <el-option
                  v-for="item in Object.keys(AuthResponseType)"
                  :key="item ?? ''"
                  :label="item ?? ''"
                  :value="item"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-form>
        </el-col>
        <el-col :span="3" class="actions-col-wrapper">
          <div class="actions-col">
            <img
              :src="require(`@/assets/images/icons/new/delete.svg`)"
              height="20"
              width="20"
              class="action-icon cursor-pointer"
              @click="deleteItem"
            />
            <el-button type="primary" size="large" @click="saveItem">
              {{ $t('save') }}
            </el-button>
          </div>
        </el-col>
      </el-row>
    </template>
  </base-card>
  <gain-configuration-versions-modal
    v-model="showVersionModal"
    :configuration-versions="props.configurationHistory"
    @restore="handleRestore"
    @close="showVersionModal = false"
  />
</template>
<script lang="ts">
import BaseCard from '@/components/panels/BaseCard.vue';
import GainConfigurationVersionsModal from './GainConfigurationVersionsModal.vue';
import { onMounted, onUpdated, reactive, ref } from 'vue';
import {
  ElButton,
  ElCol,
  ElDivider,
  ElForm,
  ElFormItem,
  ElInput,
  ElRow,
  ElSelect,
  ElOption,
  ElSwitch,
  ElTooltip
} from 'element-plus';
import {
  ApiConnectionConfiguration,
  ApiName,
  AuthGrantType,
  AuthResponseType
} from '@etp/etp-gain-bff-client/axios';
import i18n from '@/i18n';

export default {
  name: 'ApiConnectionsConfigurationCard',
  components: {
    BaseCard,
    ElButton,
    ElCol,
    ElDivider,
    ElForm,
    ElFormItem,
    ElInput,
    ElRow,
    ElSelect,
    ElOption,
    ElSwitch,
    ElTooltip,
    GainConfigurationVersionsModal
  },
  props: {
    configuration: {
      type: Object as () => ApiConnectionConfiguration,
      required: true
    },
    configurationHistory: {
      type: Array as () => Array<ApiConnectionConfiguration>,
      default: () => new Array<ApiConnectionConfiguration>()
    }
  },
  emits: ['delete', 'update', 'save', 'fetch-history'],
  setup(props, { emit }) {
    const showVersionModal = ref(false);

    const openVersionModal = () => {
      emit('fetch-history');
      showVersionModal.value = true;
    };

    const handleRestore = (input: ApiConnectionConfiguration) => {
      emit('update', input);
      showVersionModal.value = false;
    };

    interface FormType {
      enabled: boolean;
      clientId: string;
      clientSecretKeyName: string;
      tokenUrl: string;
      scope: string;
      grantType: string;
      responseType: string;
      dataEndpoint?: string;
    }

    const form = ref<FormType>({
      enabled: true,
      clientId: '',
      clientSecretKeyName: '',
      tokenUrl: '',
      scope: '',
      grantType:
        Object.keys(AuthGrantType)[0] ?? AuthGrantType.client_credentials,
      responseType: Object.keys(AuthResponseType)[0] ?? AuthResponseType.token,
      dataEndpoint: undefined
    });
    const formRef = ref<HTMLFormElement>();
    const formRules = reactive({
      clientId: [
        {
          required: true,
          message: i18n.global.t('thisFieldIsRequired')
        }
      ],
      clientSecretKeyName: [
        {
          required: true,
          message: i18n.global.t('thisFieldIsRequired')
        }
      ],
      tokenUrl: [
        {
          required: true,
          message: i18n.global.t('thisFieldIsRequired')
        }
      ],
      scope: [
        {
          required: true,
          message: i18n.global.t('thisFieldIsRequired')
        }
      ]
    });

    const emitUpdateEvent = async () => {
      emit('update', {
        enabled: form.value.enabled,
        clientId: form.value.clientId,
        clientSecretKeyName: form.value.clientSecretKeyName,
        tokenUrl: form.value.tokenUrl,
        scope: form.value.scope,
        grantType: AuthGrantType[form.value.grantType],
        responseType: AuthResponseType[form.value.responseType],
        dataEndpoint: form.value.dataEndpoint
      } as ApiConnectionConfiguration);
    };

    const saveItem = async () => {
      await formRef.value?.validate((valid: boolean) => {
        if (valid) {
          emit('save');
        }
      });
    };
    const deleteItem = async () => emit('delete');

    const handleChange = async () => {
      if (form.value.clientId) {
        form.value.clientId = form.value.clientId.trim();
      }
      if (form.value.clientSecretKeyName) {
        form.value.clientSecretKeyName = form.value.clientSecretKeyName.trim();
      }
      if (form.value.scope) {
        form.value.scope = form.value.scope.trim();
      }
      await emitUpdateEvent();
    };

    const updateLocalVars = async () => {
      form.value.enabled = props.configuration.enabled ?? true;
      form.value.clientId = props.configuration.clientId ?? '';
      form.value.clientSecretKeyName =
        props.configuration.clientSecretKeyName ?? '';
      form.value.tokenUrl = props.configuration.tokenUrl ?? '';
      form.value.scope = props.configuration.scope ?? '';
      form.value.grantType =
        Object.keys(AuthGrantType).find(
          key => AuthGrantType[key] === props.configuration.grantType
        ) ?? Object.keys(AuthGrantType)[0];
      form.value.responseType =
        Object.keys(AuthResponseType).find(
          key => AuthResponseType[key] === props.configuration.responseType
        ) ?? Object.keys(AuthResponseType)[0];
      form.value.dataEndpoint = props.configuration.dataEndpoint ?? undefined;
    };

    onMounted(async () => {
      await updateLocalVars();
      await emitUpdateEvent();
    });
    onUpdated(async () => await updateLocalVars());

    return {
      deleteItem,
      saveItem,
      form,
      formRef,
      formRules,
      handleChange,
      openVersionModal,
      showVersionModal,
      handleRestore,
      props,
      ApiName,
      AuthGrantType,
      AuthResponseType
    };
  }
};
</script>
<style lang="scss" scoped>
.broker-row {
  align-items: stretch;
}

.actions-col-wrapper {
  align-self: stretch;
  display: flex;
}

.actions-col {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  min-height: 100%;
}

:deep(.el-form-item.mb-0.is-error) {
  margin-bottom: 18px !important;
}
</style>
